import ReactPlayer from 'react-player/file'; // we only play audio, do not include everything to reduce bundle size
import React, { useState, useRef, useEffect } from 'react';
import styles from '../../styles/Player.module.css';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Image from 'next/image';
import * as LIB from '../../lib/common';
import * as TIME from './timeFormat';

const Player = React.forwardRef(({ source, article, onToggleRepeat, onToggleAuto, autoStart, lessControl }, ref) => {

    const REPEAT_OFF = '0'
    const REPEAT_ONE = '1';
    const REPEAT_ALL = '2';
    const player = useRef(null);
    const [playerState, setPlayerState] = useState('stopped');
    const [shouldPlay, setShouldPlay] = useState(false);
    const [played, setPlayed] = useState(0);
    const [seeking, setSeeking] = useState(false);
    const [repeatMode, setRepeatMode] = useState(REPEAT_ONE);
    const [autoPlay, setAutoPlay] = useState(true);
    const [duration, setDuration] = useState(0);
    const [playedTime, setPlayedTime] = useState(0);
    
    const playIcon = <Image src={LIB.getBasepath('/images/player/play.svg')} width={22} height={22}/>
    const stopIcon = <Image src={LIB.getBasepath('/images/player/stop.svg')} width={22} height={22}/>;
    const prevIcon = <Image src={LIB.getBasepath('/images/player/previous.svg')} width={22} height={22}/>;
    const prevIconOff = <Image src={LIB.getBasepath('/images/player/previous-off.svg')} width={22} height={22}/>;
    const nextIcon = <Image src={LIB.getBasepath('/images/player/next.svg')} width={22} height={22}/>;
    const nextIconOff = <Image src={LIB.getBasepath('/images/player/next-off.svg')} width={22} height={22}/>;
    const autoIconOff = <Image src={LIB.getBasepath('/images/player/repeat-off.svg')} width={22} height={22}/>;
    const autoIconOn = <Image src={LIB.getBasepath('/images/player/repeat-on.svg')} width={22} height={22}/>;
    const redoIconSlash = <Image src={LIB.getBasepath('/images/player/next.svg')} width={22} height={22}/>;
    const repeatOffIcon = <Image src={LIB.getBasepath('/images/player/repeat-single-off.svg')} width={22} height={22}/>;
    const repeatOnIcon = <Image src={LIB.getBasepath('/images/player/repeat-single-on.svg')} width={22} height={22}/>;
    const forward15sec = <Image src={LIB.getBasepath('/images/player/forward-15.svg')} width={22} height={22}/>;
    const backward15sec = <Image src={LIB.getBasepath('/images/player/backward-15.svg')} width={22} height={22}/>;

    React.useImperativeHandle(ref, () => ({
        stop() {
            if (shouldPlay) {
                setShouldPlay(false);
            }
        },
    }));

    // ==========================================================================================
    // side effect routines
    // ==========================================================================================
    useEffect(() => {
        if (typeof window !== "undefined") {
            initAutoPlay();
            initRepeatMode();
        }
    }, []);

    //auto play
    useEffect(() => {
        localStorage.setItem('autoPlay', autoPlay ? 'on' : 'off');
    }, [autoPlay]);

    //repeat
    useEffect(() => {
        if (repeatMode == REPEAT_ALL) {
            setAutoPlay(true);
        }
        localStorage.setItem('repeatMode', repeatMode);
    }, [repeatMode]);

    // ==========================================================================================
    // init setting from local storage
    // ==========================================================================================  
    const initAutoPlay = () => {
        let checkAuto = localStorage.getItem('autoPlay');

        if (checkAuto != null) {
            // not ios and not mac safari
            if (!LIB.detectIos() && !LIB.detectSafari()) {
                if (checkAuto != 'off') {
                    // alert(1);
                    setAutoPlay(true);
                    playAudio();
                }
                else {
                    setAutoPlay(false);
                }
            }
        }
        else if (autoPlay) {
            // not ios and not mac safari
            if (!LIB.detectIos() && !LIB.detectSafari()) {
                // alert(2);
                localStorage.setItem('autoPlay', 'on');
                playAudio();
            }
        }

        // not ios and not mac safari
        if (!LIB.detectIos() && !LIB.detectSafari()) {
            if (autoStart) { // set by parent element, e.g. feeding page
                playAudio();
                // console.log('player auto start');
            }
        }
    }

    const initRepeatMode = () => {
        let savedSetting = localStorage.getItem('repeatMode');

        if (savedSetting != null) {
            setRepeatMode(savedSetting);
        }
        else {
            localStorage.setItem('repeatMode', repeatMode);
        }
    }

    // ==========================================================================================
    // react-player event handlers
    // ==========================================================================================  
    const handleDuration = (duration) => {
        setDuration(duration);
    }

    const handleProgress = (progress) => {
        setPlayed(progress.played);
        setPlayedTime(progress.playedSeconds);
    }

    const handleSeekChange = (e) => {
        let t = parseFloat(e.target.value);
        player.current.seekTo(t);
        setPlayed(t);
    }

    const handleSeekMouseDown = (e) => {
        setSeeking(true);
    }

    const handleSeekMouseUp = (e) => {
        //this mouseup on is not work ios
        setSeeking(false);
        player.current.seekTo(parseFloat(e.target.value));
    }

    //callback event when play really start
    const handlePlay = () => {
        setPlayerState('playing');
    }

    //callback event when play really pause
    const handlePause = () => {
        setPlayerState('paused');
    }

    const handleOnEnd = () => {
        setPlayerState("stopped");
        setShouldPlay(false);

        // jump to next article
        if (autoPlay && repeatMode != REPEAT_ONE) {
            jumpAction('next');
        }
    }

    const handleError = () => {
        //callback event when error occurs
        setPlayerState('stopped');
    }

    // ==========================================================================================
    // UI handler functions
    // ==========================================================================================  
    const playAudio = () => {
        setPlayerState("buffering");
        setShouldPlay(true);
    }

    const togglePlayPause = () => {
        if (playerState != 'buffering') {
            setShouldPlay(!shouldPlay);
        }
    }

    const toggleRepeat = () => {
        if (typeof window !== "undefined") {
            
            let state;
            // if (repeatMode == REPEAT_OFF) state = REPEAT_ONE;
            // else if (repeatMode == REPEAT_ONE) state = REPEAT_ALL;
            // else state = REPEAT_OFF;
            if (repeatMode == REPEAT_OFF) state = REPEAT_ONE;
            else if (repeatMode == REPEAT_ONE) state = REPEAT_OFF;
            else state = REPEAT_OFF;

            setRepeatMode(state);
            onToggleRepeat(state);
        }
    }

    const toggleAutoPlay = (validDevice) => {
        if (typeof window !== "undefined") {
            if(validDevice === false){
                //invalid device
                alert("閣下的裝置不支援此功能");
            }else{
                //since we need to store it to localstorage, so we use string
                let state = (autoPlay == true ? false : true);
                setAutoPlay(state);
                onToggleAuto(state);
            }
        }
    }

    const jumpAction = (action) => {
        if (action == 'next' && article.next.id) {
            LIB.viewArticle(article.next.id, article.next.title);
        }
        else if (action == 'prev' && article.prev.id) {
            LIB.viewArticle(article.prev.id, article.prev.title);
        }
    }

    const renderRepeatBtn = () => {
        let btn = <div className={styles.playerBtn}>{repeatOffIcon}</div>;

        if (lessControl == true) {
            //no btn
            btn = <div className={styles.playerBtn}>{repeatOffIcon}</div>;
        } else if (repeatMode == REPEAT_OFF) {
            //repeat OFF
            btn = <div onClick={() => { toggleRepeat() }} className={styles.playerBtn}>{repeatOffIcon}</div>;
        } else if (repeatMode == REPEAT_ONE) {
            //repeat single one
            btn = <div onClick={() => { toggleRepeat() }} className={styles.playerBtn}>{repeatOnIcon}</div>;
        } else {
            //repeat ON
            btn = <div onClick={() => { toggleRepeat() }} className={styles.playerBtn}>{repeatOnIcon}</div>;
        }

        return (
            <>{btn}</>
        );
    }

    const renderAutoBtn = () => {
        let btn = <div className={styles.playerBtn}>{autoIconOff}</div>;

        if (LIB.detectIos() || LIB.detectSafari() || lessControl === true) {
            btn = <div className={styles.playerBtn} onClick={() => { toggleAutoPlay(false) }}>{autoIconOff}</div>;
        } else {
            btn = <div className={styles.playerBtn} onClick={() => { toggleAutoPlay() }}>{autoPlay ? autoIconOn : autoIconOff}</div>;
        }
        return btn;
    }

    const renderNextBtn = () => {
        if (article && article.next.id !== null) {
            return (
                <div className={styles.playerBtn} onClick={() => { jumpAction('next') }}>{nextIcon}</div>
            );
        } else {
            // no next page
            return (
                <div className={styles.playerBtn}>{nextIconOff}</div>
            );
        }
    }

    const renderPrevBtn = () => {
        if (article && article.prev.id !== null) {
            return (
                <div className={styles.playerBtn} onClick={() => { jumpAction('prev') }}>{prevIcon}</div>
            );
        } else {
            return (
                <div className={styles.playerBtn}>{prevIconOff}</div>
            );
        }
    }

    function getOpacity() {
        return playerState == 'buffering' ? { opacity: 0.5 } : { opacity: 1 };
    }

    const renderForward15sec = () => {
        return (
            <div className={styles.playerBtn} onClick={() => { updateForwardBackwardTime('f') }}>{forward15sec}</div>
        );
    }

    const renderBackward15sec = () => {
        return (
            <div className={styles.playerBtn} onClick={() => { updateForwardBackwardTime('b') }}>{backward15sec}</div>
        );
    }

    const updateForwardBackwardTime = (action) => {
        if (action == 'f') {
            //forward 5 sec.
            player.current.seekTo(parseFloat(playedTime + 15));
        } else if (action == 'b') {
            //backward 5 sec.
            player.current.seekTo(parseFloat(playedTime - 15));
        }
    }

    // ==========================================================================================
    // render
    // ==========================================================================================  

    return (
        <div className={styles.playerWrapper}>
            <div className={styles.player}>
                <ReactPlayer
                    ref={player}
                    url={source}
                    width="1px"
                    height="1px"
                    controls={false}
                    playing={shouldPlay}
                    onDuration={handleDuration}
                    onProgress={handleProgress}
                    onEnded={handleOnEnd}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onError={handleError}
                    loop={repeatMode == REPEAT_ONE ? true : false}
                />

                <div className={styles.rangeContainer}>
                    <div className={styles.rangeHolder} style={getOpacity()}>
                        <input
                            className={styles.range}
                            type='range'
                            min={0} max={0.999999}
                            step={0.00001}
                            value={played}
                            tooltip="off"
                            onMouseDown={handleSeekMouseDown}
                            onChange={handleSeekChange}
                            onMouseUp={handleSeekMouseUp}
                            style={{ 'background': `linear-gradient(to right, #99c8dd ${played * 100}%, #fff 0px` }}
                        />
                    </div>
                </div>

                <div className={styles.timeContainer}>
                    <div className={styles.timeCurrent}>{TIME.format(playedTime)} / {TIME.format(duration)}</div>
                    <div className={styles.timeTotal}>-{TIME.format(duration - playedTime)}</div>
                </div>

                <Container fluid className={styles.btnHolder}>
                    <Row>
                        <Col xs={12}>
                            <div className={styles.center}>
                                {renderAutoBtn()}
                                {renderBackward15sec()}
                                {renderPrevBtn()}
                                <div className={styles.playerBtn} id='playBtn' style={getOpacity()} onClick={togglePlayPause}>{!shouldPlay ? playIcon : stopIcon}</div>
                                {renderNextBtn()}
                                {renderForward15sec()}
                                {renderRepeatBtn()}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
})

export default Player;